package top.wcpe.allincore.ui.runtime.render.transform

import top.wcpe.allincore.ui.runtime.element.UIElement
import kotlin.jvm.JvmStatic
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.tan

/**
 * 4×4 列优先矩阵工具：用于 3D 变换（rotateX/rotateY + CSS-style perspective）。
 *
 * 本工具只在 RenderStateStack 检测到元素含 rotateX/rotateY 或非默认 perspective 时使用，
 * 否则纯 2D 变换继续走 [AffineMatrix3] 3×3 路径，避免无谓的 4×4 开销。
 *
 * 矩阵布局（列优先，与 JOML Matrix4f.get(float[]) 完全一致）：
 * ```
 * out[0..3]   = 第 1 列
 * out[4..7]   = 第 2 列
 * out[8..11]  = 第 3 列
 * out[12..15] = 第 4 列
 * ```
 */
// 矩阵索引与单位阵 0/1 系数是线性代数本征值，非配置型魔法值，整体豁免 MagicNumber。
@Suppress("MagicNumber")
object Matrix4x4Builder {

    /**
     * 构建元素 3D 局部变换矩阵：T(cx,cy) × P × Rz × Rx × Ry × Skew × S × T(-cx,-cy) × T(tx,ty)。
     *
     * 顺序约定（CSS transform 习惯）：先 translate，再 scale，再 skew，再 Ry / Rx / Rz，
     * 最后透视除法。旋转/透视中心是元素自身中心 (cx, cy)，由调用方传入屏幕绝对坐标。
     *
     * @param el 元素（读 translate/scale/skew/rotation/rotateX/rotateY/perspective）
     * @param cx 元素中心 X（屏幕绝对坐标）
     * @param cy 元素中心 Y（屏幕绝对坐标）
     * @return 列优先 16 元素数组
     */
    @JvmStatic
    fun fromElement(el: UIElement<*>, cx: Float, cy: Float): FloatArray {
        // 工作矩阵存为列优先 16 元素数组，单位矩阵起步
        val m = identity()

        // 1) 平移到元素中心（让旋转/透视中心是元素自身中心）
        translate(m, cx, cy, 0f)

        // 2) 透视：CSS perspective(d) 对应 1/w = 1 - z/d
        if (el.perspective() != UIElement.DEFAULT_PERSPECTIVE) {
            applyPerspective(m, el.perspective())
        }

        // 3) 旋转：Z 轴（rotation），X 轴（rotateX），Y 轴（rotateY）
        if (el.rotation() != 0f) {
            rotateZ(m, el.rotation())
        }
        if (el.rotateX() != 0f) {
            rotateX(m, el.rotateX())
        }
        if (el.rotateY() != 0f) {
            rotateY(m, el.rotateY())
        }

        // 4) 倾斜（2D skew，独立于 3D 旋转）
        if (el.skewX() != 0f || el.skewY() != 0f) {
            skew(m, el.skewX(), el.skewY())
        }

        // 5) 缩放
        if (el.scaleX() != 1f || el.scaleY() != 1f) {
            scale(m, el.scaleX(), el.scaleY(), 1f)
        }

        // 6) 平移回元素位置
        translate(m, -cx, -cy, 0f)

        // 7) 平移属性（translateX/translateY 是元素自身局部偏移）
        if (el.translateX() != 0f || el.translateY() != 0f) {
            translate(m, el.translateX(), el.translateY(), 0f)
        }

        return m
    }

    /** 4×4 单位矩阵（列优先）。 */
    @JvmStatic
    fun identity(): FloatArray = floatArrayOf(
        1f, 0f, 0f, 0f,
        0f, 1f, 0f, 0f,
        0f, 0f, 1f, 0f,
        0f, 0f, 0f, 1f,
    )

    /** 右乘平移矩阵：m = m × T(tx, ty, tz)。 */
    private fun translate(m: FloatArray, tx: Float, ty: Float, tz: Float) {
        // 平移只改第 4 列：col4 += col1*tx + col2*ty + col3*tz
        m[12] += m[0] * tx + m[4] * ty + m[8] * tz
        m[13] += m[1] * tx + m[5] * ty + m[9] * tz
        m[14] += m[2] * tx + m[6] * ty + m[10] * tz
        m[15] += m[3] * tx + m[7] * ty + m[11] * tz
    }

    /** 右乘缩放矩阵：m = m × S(sx, sy, sz)。 */
    private fun scale(m: FloatArray, sx: Float, sy: Float, sz: Float) {
        m[0] *= sx
        m[1] *= sx
        m[2] *= sx
        m[3] *= sx
        m[4] *= sy
        m[5] *= sy
        m[6] *= sy
        m[7] *= sy
        m[8] *= sz
        m[9] *= sz
        m[10] *= sz
        m[11] *= sz
    }

    /** 右乘绕 Z 轴旋转（标准 2D 旋转）。 */
    private fun rotateZ(m: FloatArray, rad: Float) {
        val c = cos(rad.toDouble()).toFloat()
        val s = sin(rad.toDouble()).toFloat()
        // 仅影响第 1、2 列
        val m00 = m[0]
        val m01 = m[1]
        val m02 = m[2]
        val m03 = m[3]
        val m10 = m[4]
        val m11 = m[5]
        val m12 = m[6]
        val m13 = m[7]
        m[0] = m00 * c + m10 * s
        m[1] = m01 * c + m11 * s
        m[2] = m02 * c + m12 * s
        m[3] = m03 * c + m13 * s
        m[4] = m00 * -s + m10 * c
        m[5] = m01 * -s + m11 * c
        m[6] = m02 * -s + m12 * c
        m[7] = m03 * -s + m13 * c
    }

    /** 右乘绕 X 轴旋转（绕水平轴翻转，Y 与 Z 互换）。 */
    private fun rotateX(m: FloatArray, rad: Float) {
        val c = cos(rad.toDouble()).toFloat()
        val s = sin(rad.toDouble()).toFloat()
        // 仅影响第 2、3 列
        val m10 = m[4]
        val m11 = m[5]
        val m12 = m[6]
        val m13 = m[7]
        val m20 = m[8]
        val m21 = m[9]
        val m22 = m[10]
        val m23 = m[11]
        m[4] = m10 * c + m20 * s
        m[5] = m11 * c + m21 * s
        m[6] = m12 * c + m22 * s
        m[7] = m13 * c + m23 * s
        m[8] = m10 * -s + m20 * c
        m[9] = m11 * -s + m21 * c
        m[10] = m12 * -s + m22 * c
        m[11] = m13 * -s + m23 * c
    }

    /** 右乘绕 Y 轴旋转（绕竖直轴翻转，X 与 Z 互换）。 */
    private fun rotateY(m: FloatArray, rad: Float) {
        val c = cos(rad.toDouble()).toFloat()
        val s = sin(rad.toDouble()).toFloat()
        // 仅影响第 1、3 列
        val m00 = m[0]
        val m01 = m[1]
        val m02 = m[2]
        val m03 = m[3]
        val m20 = m[8]
        val m21 = m[9]
        val m22 = m[10]
        val m23 = m[11]
        m[0] = m00 * c + m20 * -s
        m[1] = m01 * c + m21 * -s
        m[2] = m02 * c + m22 * -s
        m[3] = m03 * c + m23 * -s
        m[8] = m00 * s + m20 * c
        m[9] = m01 * s + m21 * c
        m[10] = m02 * s + m22 * c
        m[11] = m03 * s + m23 * c
    }

    /** 右乘 2D skew 矩阵（仅 X/Y 平面）。 */
    private fun skew(m: FloatArray, sx: Float, sy: Float) {
        val tx = tan(sx.toDouble()).toFloat()
        val ty = tan(sy.toDouble()).toFloat()
        // skew 矩阵 column-major:
        //   col1=(1, ty, 0, 0), col2=(tx, 1, 0, 0), col3=(0,0,1,0), col4=(0,0,0,1)
        // m' = m × skew
        val m00 = m[0]
        val m01 = m[1]
        val m02 = m[2]
        val m03 = m[3]
        val m10 = m[4]
        val m11 = m[5]
        val m12 = m[6]
        val m13 = m[7]
        m[0] = m00 + m10 * ty
        m[1] = m01 + m11 * ty
        m[2] = m02 + m12 * ty
        m[3] = m03 + m13 * ty
        m[4] = m00 * tx + m10
        m[5] = m01 * tx + m11
        m[6] = m02 * tx + m12
        m[7] = m03 * tx + m13
    }

    /**
     * 右乘 CSS-style 透视矩阵：
     * ```
     * | 1  0  0     0 |
     * | 0  1  0     0 |
     * | 0  0  1     0 |
     * | 0  0 -1/d   1 |
     * ```
     * 列优先存储下 P 的第 3 列为 (0, 0, 1, -1/d)，其它列不变。
     * 因此 m × P 只改 col3：col3' = m_col3 + m_col4 × (-1/d)。
     * d 越小透视越强；与 CSS perspective() 语义一致。
     */
    private fun applyPerspective(m: FloatArray, d: Float) {
        if (d <= 0f) {
            return
        }
        val invD = -1f / d
        m[8] += m[12] * invD
        m[9] += m[13] * invD
        m[10] += m[14] * invD
        m[11] += m[15] * invD
    }
}
